import { useDependencies } from '@servicetitan/react-ioc';
import { PrequalsApi, PreApprovalDto } from '@servicetitan/financing-api-client/src/prequals-api';
import { useQuery, UseQueryResult } from '@tanstack/react-query';

export interface UseLoadPreApprovalsOptions {
    /**
     * Whether the query is allowed to run (default: true). Combined with the tenant/customer/bu
     * presence gate, so an app root can skip the load on prequal/loan flows by passing
     * `{ enabled: false }`.
     */
    enabled?: boolean;
}

/**
 * Fetches the latest non-expired pre-approval for a customer.
 *
 * Enabled only when tenantId, customerId, and buId are all present (mirrors useLoadPrequals) and
 * the optional `enabled` flag is not false. Swallows ALL errors -> null (not just 404) the way
 * useLoansWithNoJob swallows errors -> empty: a transient failure fails safe to "no pre-approval"
 * and the query always settles for the readiness gate.
 */
export const useLoadPreApprovals = (
    tenantId: string,
    customerId: string,
    buId: string,
    options?: UseLoadPreApprovalsOptions,
): UseQueryResult<PreApprovalDto | null> => {
    const [apiClient] = useDependencies(PrequalsApi);

    return useQuery({
        queryKey: ['preApprovals', tenantId, customerId, buId],
        enabled: (options?.enabled ?? true) && !!tenantId && !!customerId && !!buId,
        queryFn: async (): Promise<PreApprovalDto | null> => {
            try {
                const response = await apiClient.preApprovalsGetPreApproval({
                    tenantId,
                    customerId,
                    buId,
                });
                return response.data ?? null;
            } catch {
                // Any error (404 = no pre-approval, 5xx, network) -> no pre-approval. Fail safe.
                return null;
            }
        },
    });
};
